import React, { useEffect, useRef, useState } from 'react';

interface LogoProcessorProps {
  src: string;
  alt: string;
  className?: string;
}

const LogoProcessor: React.FC<LogoProcessorProps> = ({ src, alt, className }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [processedSrc, setProcessedSrc] = useState<string>('');

  useEffect(() => {
    const processImage = async () => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Set canvas size
        const size = Math.min(img.width, img.height);
        canvas.width = size;
        canvas.height = size;

        // Clear canvas with transparent background
        ctx.clearRect(0, 0, size, size);

        // Create circular clipping path
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
        ctx.clip();

        // Draw the image centered
        const offsetX = (size - img.width) / 2;
        const offsetY = (size - img.height) / 2;
        ctx.drawImage(img, offsetX, offsetY);

        // Get image data
        const imageData = ctx.getImageData(0, 0, size, size);
        const data = imageData.data;

        // Process pixels: convert to white cutaway
        for (let i = 0; i < data.length; i += 4) {
          // Get RGB values
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];

          // Calculate luminance
          const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

          // If pixel is dark (logo part), make it white
          // If pixel is light (background), make it transparent
          if (luminance < 0.5) {
            // Make it white
            data[i] = 255;     // R
            data[i + 1] = 255; // G
            data[i + 2] = 255; // B
            data[i + 3] = a;   // Keep original alpha
          } else {
            // Make it transparent
            data[i + 3] = 0;   // Alpha
          }
        }

        // Put processed image back
        ctx.putImageData(imageData, 0, 0);

        // Apply circular mask again to ensure clean edges
        ctx.globalCompositeOperation = 'destination-in';
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
        ctx.fill();

        // Convert to data URL
        setProcessedSrc(canvas.toDataURL('image/png'));
      };

      img.src = src;
    };

    processImage();
  }, [src]);

  return (
    <>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      {processedSrc && (
        <img 
          src={processedSrc} 
          alt={alt} 
          className={className}
        />
      )}
    </>
  );
};

export default LogoProcessor;